import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { merge, Subject } from 'rxjs';
import { filter, switchMap, take, takeUntil } from 'rxjs/operators';

import { CompanyModel } from '@data-layer/models/companies';
import { ShipmentModel, SimilarHtsSuggestionModel } from '@data-layer/models/common';
import { FavoriteHtsNumberModel } from '@data-layer/models/common/favorite-hts-number.model';
import { EntrySummaryModel, InvoiceModel, LineModel } from '@data-layer/models/entry-summaries';
import { EntrySummaryActionsHelper } from '@helpers/entry-summary-actions.helper';
import { FormHelper } from '@helpers/form.helper';
import { CompaniesService } from '@services/companies.service';
import { IHtsAssistantHtsFound, IHtsAssistantLineRef } from '@shared/hts-assistant/interfaces';
import { EntrySummaryStateService } from '../../../services/entry-summary-state.service';
import { EsLineItemFormDesktopComponent } from '../es-line-item-form-desktop/es-line-item-form-desktop.component';

/**
 * Line panel of the classification assistant's Validate view (#13384 F7): the invoice table row of the selected line
 * (with the column header) above the full line form and a Save button. Rendered by the drawer through
 * `[dynamic-component]` (the hts-assistant module cannot import es-mutual), so the drawer only knows the `Type`;
 * `IHtsAssistantPanelInputs` documents the input names the drawer binds.
 * The form is re-created per line (keyed `*ngFor`): unsaved edits are dropped on a line switch (product decision).
 * Read mode by default (F15): the pencil switches the form to edit mode, which holds the assistant until Save — the user
 * works either in the panel or in the chat, never both. A line without a description starts in edit mode with the field
 * flagged. Insert pushes only the code into the form (`applied`, already persisted); "Find in catalog" opens the Bina
 * catalog inside the panel (another as-window would replace the drawer) and its pick makes the form dirty for Save.
 */
@Component({
  selector: 'es-hts-line-panel',
  templateUrl: './es-hts-line-panel.component.html',
  styleUrls: ['./es-hts-line-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EsHtsLinePanelComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @Input() public lineRef: IHtsAssistantLineRef | null = null;
  @Input() public isFirst: boolean = false;
  @Input() public isLast: boolean = false;
  @Input() public applied: IHtsAssistantHtsFound | null = null;
  /** The line has no saved description: start in edit mode with the description flagged. */
  @Input() public isForceEdit: boolean = false;
  /** Validate view: Back / Next arrows; the standalone drawer shows one line and hides them. */
  @Input() public isNavigable: boolean = true;

  @Output() public prev: EventEmitter<void> = new EventEmitter<void>();
  @Output() public next: EventEmitter<void> = new EventEmitter<void>();
  @Output() public close: EventEmitter<void> = new EventEmitter<void>();
  /** The line was persisted and the entry reloaded. */
  @Output() public saved: EventEmitter<LineModel> = new EventEmitter<LineModel>();
  /** Unsaved user edits in the form (Insert does not count): the drawer asks before closing / switching the line. */
  @Output() public dirtyChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  /** Edit mode on / off: the drawer holds the assistant while the line is being edited (#13384 F15). */
  @Output() public editModeChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  @ViewChildren(EsLineItemFormDesktopComponent)
  public forms: QueryList<EsLineItemFormDesktopComponent>;

  /** `[lineRef]` — the keyed `*ngFor` re-creates the table and the form only when the line id changes. */
  public lineRefs: IHtsAssistantLineRef[] = [];
  public shipment: ShipmentModel | null = null;
  /** The real invoice goes to the form; the table gets a copy holding only the selected line. */
  public invoice: InvoiceModel | null = null;
  public tableInvoice: InvoiceModel | null = null;
  public isSaving: boolean = false;
  public isCatalogOpen: boolean = false;
  public isEditMode: boolean = false;
  /** The same gate the form applies to itself (`EntrySummaryActionsHelper.isCanEdit`): no pencil on a locked entry. */
  public isEntryEditable: boolean = false;
  public company: CompanyModel | null = null;

  private form: EsLineItemFormDesktopComponent | null = null;
  private boundLineId: number | null = null;
  private appliedSeen: IHtsAssistantHtsFound | null = null;
  private isDirtyReported: boolean = false;
  private formReplaced$: Subject<void> = new Subject<void>();
  private isDestroy: Subject<void> = new Subject<void>();

  constructor(
    private entrySummaryStateService: EntrySummaryStateService,
    private companiesService: CompaniesService,
    private fh: FormHelper,
    private cdr: ChangeDetectorRef,
    private elementRef: ElementRef<HTMLElement>,
  ) {}

  public ngOnInit(): void {
    this.company = this.companiesService.getCompany() || null;
    this.entrySummaryStateService.entrySummary$
      .pipe(
        filter((entry: EntrySummaryModel) => !!entry),
        takeUntil(this.isDestroy),
      )
      .subscribe(() => {
        this.resolve();
        this.cdr.markForCheck();
      });
  }

  /**
   * Also called by `[dynamic-component]` by hand on every inputs change (a new inputs object comes with every line switch
   * AND every entry reload). Only a line switch rebuilds the view; entry reloads are followed through `entrySummary$`.
   */
  public ngOnChanges(): void {
    // inputs are assigned imperatively by the directive: nothing else marks this OnPush view dirty (isFirst / isLast too)
    this.cdr.markForCheck();
    this.applyInserted();
    const lineId: number | null = this.lineRef ? this.lineRef.lineId : null;
    if (lineId === this.boundLineId) {
      return;
    }
    this.boundLineId = lineId;
    this.isCatalogOpen = false;
    this.setEditMode(this.isForceEdit);
    this.lineRefs = this.lineRef ? [this.lineRef] : [];
    // detach the outgoing form right away: the Save button must not read its dirty state while the view is rebuilt
    this.formReplaced$.next();
    this.form = null;
    this.reportDirty();
    this.resolve();
  }

  public ngAfterViewInit(): void {
    this.bindForm();
    this.forms.changes.pipe(takeUntil(this.isDestroy)).subscribe(() => this.bindForm());
  }

  public ngOnDestroy(): void {
    this.form = null;
    this.reportDirty();
    this.setEditMode(false);
    this.formReplaced$.next();
    this.formReplaced$.complete();
    this.isDestroy.next();
    this.isDestroy.complete();
  }

  public get indexOffset(): number {
    return this.lineRef ? this.lineRef.index - 1 : 0;
  }

  public get shipmentExportDate(): string | null {
    return this.shipment ? this.shipment.exportDate : null;
  }

  public get isSaveDisabled(): boolean {
    return this.isSaving || !this.form || !this.form.itemForm || !this.form.itemForm.dirty;
  }

  public trackByLineId(index: number, ref: IHtsAssistantLineRef): number {
    return ref.lineId;
  }

  public get isShowEditButton(): boolean {
    return !this.isEditMode && this.isEntryEditable && !!this.invoice;
  }

  /** Always offered while editing; on a line without a description the field stays flagged in read mode afterwards. */
  public get isShowCancelButton(): boolean {
    return this.isEditMode;
  }

  /** The footer sits outside the scrolling body (#13528); while the catalog covers the panel it has nothing to act on. */
  public get isShowFooter(): boolean {
    return !this.isCatalogOpen && (this.isEditMode || this.isShowEditButton);
  }

  /** Edit: the form becomes editable (`[isReadMode]` binding) and the assistant waits until Save or Cancel. */
  public enterEditMode(): void {
    if (this.isEditMode || !this.isEntryEditable) {
      return;
    }
    this.setEditMode(true);
    if (this.isForceEdit && this.form) {
      // the assistant's own hint (not the generic "required") and the focus, once the form is enabled by the binding
      const form: EsLineItemFormDesktopComponent = this.form;
      setTimeout(() => form.requireDescriptionForAssistant());
    }
    this.cdr.markForCheck();
  }

  /** Cancel: unsaved edits are dropped, the stored line is shown again, the assistant resumes. No confirm — it is explicit. */
  public onCancel(): void {
    if (this.form) {
      this.form.revert();
    }
    this.reportDirty();
    this.setEditMode(false);
    this.cdr.markForCheck();
  }

  public openCatalog(): void {
    this.isCatalogOpen = true;
    this.scrollToTop();
  }

  public closeCatalog(): void {
    this.isCatalogOpen = false;
    this.scrollToTop();
  }

  /** The catalog opens narrowed to the line's current description. */
  public get catalogInitialSearch(): string | null {
    return this.form && this.form.itemForm ? this.form.itemForm.get('description').value : null;
  }

  /**
   * A catalog pick: tagged companies go through the backend (the form saves its draft first, the code is written by the
   * backend); otherwise it is an unsaved edit like any other — the form goes dirty and Save persists it.
   */
  public onCatalogInsert(favoriteHtsNumber: FavoriteHtsNumberModel): void {
    if (this.form && this.form.itemForm) {
      this.form.onCatalogPick(favoriteHtsNumber);
      if (!this.form.isBackendInsert) {
        this.form.itemForm.markAsDirty();
      }
      this.reportDirty();
    }
    this.isCatalogOpen = false;
    this.scrollToTop();
    this.cdr.markForCheck();
  }

  public onSuggestionInsert(suggestion: SimilarHtsSuggestionModel): void {
    if (this.form) {
      this.form.onSuggestionPick(suggestion);
    }
    this.isCatalogOpen = false;
    this.scrollToTop();
    this.cdr.markForCheck();
  }

  /** The pre-save may have gone through (form pristine) before the insert was refused: the drawer must see the real state. */
  public onLineInsertFailed(): void {
    this.reportDirty();
    this.cdr.markForCheck();
  }

  /** The backend wrote the code onto the saved line: nothing left to save here — back to read mode, the assistant resumes. */
  public onLineInserted(): void {
    if (this.form && this.form.itemForm) {
      this.form.itemForm.markAsPristine();
    }
    this.reportDirty();
    this.setEditMode(false);
    this.cdr.markForCheck();
  }

  public onSave(): void {
    if (this.form && !this.isSaving) {
      this.form.submit();
    }
  }

  /** The form validated its value; persisting is the host's job (same contract as the summary page). */
  public onLineDataSaved(payload: LineModel): void {
    const ref: IHtsAssistantLineRef | null = this.lineRef;
    const entry: EntrySummaryModel | null = this.entrySummaryStateService.entrySummary$.getValue();
    if (!ref || !entry) {
      return;
    }
    this.isSaving = true;
    this.cdr.markForCheck();
    // Not cut by `takeUntil`: once the PUT is out, the reload must land in the store even if the panel closes meanwhile.
    // A failed reload goes to the error path like on the summary / invoice pages: the form stays dirty and Save retries
    // (a second PUT of the same values is harmless), so the store is never left stale behind a "saved" form.
    this.entrySummaryStateService
      .updateLineItem(this.fh.emptyStringsToNull(payload), ref.lineId)
      .pipe(
        switchMap(() => this.entrySummaryStateService.loadEntrySummary(entry.referenceNumber)),
        take(1),
      )
      .subscribe(
        (reloaded: EntrySummaryModel) => {
          this.isSaving = false;
          // pristine BEFORE the store update: the form re-patches itself from the entry only while it is not dirty.
          // Only the form of the saved line: the user may have switched to another line while the PUT was in flight.
          if (this.form && this.form.itemForm && this.lineRef && this.lineRef.lineId === ref.lineId) {
            this.form.itemForm.markAsPristine();
            this.reportDirty();
            // saved: back to read mode — the assistant resumes (and restarts on its own if the description changed)
            this.setEditMode(false);
          }
          this.entrySummaryStateService.updateCurrentEntrySummary(reloaded);
          this.saved.emit(payload);
          this.cdr.markForCheck();
        },
        () => {
          // the global alert is already shown; the form stays dirty so the user can retry
          this.isSaving = false;
          this.cdr.markForCheck();
        },
      );
  }

  /** Insert overwrites description + code in the form (they are stored already); other unsaved edits are kept. */
  private applyInserted(): void {
    if (!this.applied || this.applied === this.appliedSeen) {
      return;
    }
    this.appliedSeen = this.applied;
    if (this.form && this.form.itemForm) {
      this.form.applyHts(this.applied);
    }
    this.cdr.markForCheck();
  }

  /** The drawer's panel box (our parent) owns the scrolling: a content swap should not keep a stale offset. */
  private scrollToTop(): void {
    const container: HTMLElement | null = this.elementRef.nativeElement.parentElement;
    if (container) {
      container.scrollTop = 0;
    }
  }

  private setEditMode(isEditMode: boolean): void {
    if (isEditMode === this.isEditMode) {
      return;
    }
    this.isEditMode = isEditMode;
    this.editModeChange.emit(isEditMode);
  }

  private bindForm(): void {
    this.formReplaced$.next();
    this.form = this.forms.first || null;
    if (this.form && this.form.itemForm) {
      // a line without a description starts in edit mode with the field flagged — on the next tick: the query list
      // settles after this view was checked, and flipping the control's errors now would be a change after the check
      if (this.isEditMode && this.isForceEdit) {
        const form: EsLineItemFormDesktopComponent = this.form;
        setTimeout(() => form.requireDescriptionForAssistant());
      }
      // keeps the Save button in step with the form's dirty state under OnPush (UI input marks dirty before it emits)
      this.form.itemForm.valueChanges.pipe(takeUntil(merge(this.formReplaced$, this.isDestroy))).subscribe(() => {
        this.reportDirty();
        this.cdr.markForCheck();
      });
    }
    this.reportDirty();
    this.cdr.markForCheck();
  }

  /** `markAsDirty` / `markAsPristine` emit nothing, so every place that flips the flag calls this explicitly. */
  private reportDirty(): void {
    const isDirty: boolean = !!(this.form && this.form.itemForm && this.form.itemForm.dirty);
    if (isDirty !== this.isDirtyReported) {
      this.isDirtyReported = isDirty;
      this.dirtyChange.emit(isDirty);
    }
  }

  private resolve(): void {
    const entry: EntrySummaryModel | null = this.entrySummaryStateService.entrySummary$.getValue();
    const ref: IHtsAssistantLineRef | null = this.lineRef;
    this.shipment = null;
    this.invoice = null;
    this.tableInvoice = null;
    this.isEntryEditable = !!entry && EntrySummaryActionsHelper.isCanEdit(entry);
    if (!entry || !ref) {
      return;
    }
    const shipment: ShipmentModel | null = (entry.shipments || []).find((item: ShipmentModel) => item.id === ref.shipmentId) || null;
    const invoice: InvoiceModel | null = shipment
      ? (shipment.invoices || []).find((item: InvoiceModel) => item.id === ref.invoiceId) || null
      : null;
    const line: LineModel | null = invoice ? (invoice.lines || []).find((item: LineModel) => item.id === ref.lineId) || null : null;
    this.shipment = shipment;
    this.invoice = invoice;
    this.tableInvoice = invoice && line ? { ...invoice, lines: [line] } : null;
  }
}
